import React, { memo, useCallback, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { Box, Button } from '@mui/material';
import FormGenerator from '@sharedCR/common/formGenerator';
import { FormNames } from 'app/services/autoLockForms/autoLockForms.constants';
import { WithLoadingPropTypes } from 'hocs/withLoading';
import PropTypes from 'prop-types';

import { ConfirmationModalType } from 'constants/confirmationDialog';
import { EmployerPhoneSource, EmploymentVerificationType } from 'constants/loanApplication';
import { HistoryPendingChangesActionType } from 'constants/pendingChanges';
import useForm from 'hooks/useForm';
import { getConfigInfo } from 'utils/loanManagement';
import { mergePendingChanges } from 'utils/pendingChanges';

import { openConfirmationDialogAction } from 'store/global/actions';

import { getEmploymentVerificationConfig } from './EmploymentAndIncome.config';
import {
  employmentVerificationContainerStyles,
  employmentVerificationFormControlsStyles,
} from './EmploymentAndIncome.styles';
import { EmploymentAndIncomeTestIds } from './EmploymentAndIncome.testIds';

const EmploymentVerificationForm = ({
  employmentVerificationData,
  isLoading,
  cancelForm,
  pendingChanges,
  saveForm,
  isSelfEmployedIncomeType = false,
}) => {
  const dispatch = useDispatch();

  const observableFieldsNames = Object.keys(employmentVerificationData);
  const isFundingType =
    employmentVerificationData.verificationType === EmploymentVerificationType.FUNDING;

  const { initialFormValues, schema, fieldsConfig } = useMemo(() => {
    const initialConfig = getEmploymentVerificationConfig({
      isSelfEmployedIncomeType,
      isFundingType,
      isEmployerPhoneSourceOther:
        employmentVerificationData.employerPhoneSource === EmployerPhoneSource.OTHER,
    });

    return getConfigInfo({
      applicationData: employmentVerificationData,
      config: initialConfig,
      isNestedForm: true,
    });
  }, [employmentVerificationData, isSelfEmployedIncomeType, isFundingType]);

  const {
    form,
    handleChange,
    validationErrors,
    setSchema,
    setInForm,
    isFormChanged,
    validateField,
    validateForm,
    staticValidateField,
    formPendingChanges,
  } = useForm(initialFormValues, fieldsConfig, observableFieldsNames);

  const isEmployerPhoneSourceOther = form.employerPhoneSource?.value === EmployerPhoneSource.OTHER;

  const { mergedPendingChanges, isPendingChangesHighlightDisabled } = useMemo(() => {
    const mergedPendingChanges = mergePendingChanges(pendingChanges, formPendingChanges);

    return {
      mergedPendingChanges,
      isPendingChangesHighlightDisabled: Object.values(mergedPendingChanges).some(
        ({ actionType }) => actionType === HistoryPendingChangesActionType.ADDED,
      ),
    };
  }, [formPendingChanges, pendingChanges]);

  const formGeneratorConfig = useMemo(() => {
    return getEmploymentVerificationConfig({
      isSelfEmployedIncomeType,
      isFundingType,
      isEmployerPhoneSourceOther,
    });
  }, [isSelfEmployedIncomeType, isFundingType, isEmployerPhoneSourceOther]);

  useEffect(() => {
    setSchema(schema);
  }, [setSchema, schema]);

  useEffect(() => {
    const { schema } = getConfigInfo({ applicationData: form, config: formGeneratorConfig });

    setSchema(schema);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setSchema, form.otherEmployerPhoneSourceDescription]);

  const onSubmit = useCallback(
    async e => {
      e.preventDefault();

      if (!(await validateForm())) {
        return;
      }

      saveForm({ updatedData: form, initialData: employmentVerificationData });
    },
    [form, validateForm, saveForm, employmentVerificationData],
  );

  const onCancelClick = useCallback(() => {
    if (!isFormChanged) {
      cancelForm(employmentVerificationData);

      return;
    }

    dispatch(
      openConfirmationDialogAction({
        type: ConfirmationModalType.WARNING,
        message: 'Cancel Changes',
        description: 'Are you sure you want to cancel changes?',
        onAccept: () => {
          cancelForm(employmentVerificationData);
        },
      }),
    );
  }, [dispatch, isFormChanged, cancelForm, employmentVerificationData]);

  return (
    <form noValidate data-ischanged={isFormChanged} onSubmit={onSubmit}>
      <Box sx={employmentVerificationContainerStyles}>
        <FormGenerator
          isNestedForm
          config={formGeneratorConfig}
          disabledPendingChangesHighlight={isPendingChangesHighlightDisabled}
          form={form}
          formName={FormNames.EMPLOYMENT_VERIFICATION}
          handleChange={handleChange}
          isLoading={isLoading}
          numberOfColumns={4}
          pendingChanges={mergedPendingChanges}
          setInForm={setInForm}
          staticValidateField={staticValidateField}
          validateField={validateField}
          validationErrors={validationErrors}
        />
        <Box sx={employmentVerificationFormControlsStyles}>
          <Button
            data-testid={EmploymentAndIncomeTestIds.VERIFICATION_FORM_CANCEL_BUTTON}
            size="small"
            variant="line"
            onClick={onCancelClick}
          >
            Cancel
          </Button>
          <Button
            data-testid={EmploymentAndIncomeTestIds.VERIFICATION_FORM_ADD_BUTTON}
            disabled={!isFormChanged}
            size="small"
            type="submit"
          >
            Add
          </Button>
        </Box>
      </Box>
    </form>
  );
};

EmploymentVerificationForm.propTypes = {
  employmentVerificationData: PropTypes.object.isRequired,
  cancelForm: PropTypes.func.isRequired,
  saveForm: PropTypes.func.isRequired,
  pendingChanges: PropTypes.object,
  isSelfEmployedIncomeType: PropTypes.bool,
  ...WithLoadingPropTypes,
};

export default memo(EmploymentVerificationForm);
